import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Calendar, MapPin, ArrowRight, Users, Trophy, ChevronLeft, ChevronRight, Music } from "lucide-react";
import PageTransition from "../components/PageTransition";
import GutscheinKaufen from "../components/GutscheinKaufen";

const events = [
  {
    id: 3,
    title: "Frühjahrsüberholung der Plätze",
    date: "01.04 – 02.04",
    location: "Sportanlage Heinersreuth",
    tag: "Pflege",
    images: [
      "/photos/tennis-court.jpg",
      "/photos/padel-on-court.jpg",
    ],
    icon: <Calendar className="w-4 h-4" />,
    accentColor: "text-green-400",
    shortDesc: "Die Firma aus Tschechien übernimmt die Frühjahrsüberholung der Tennisplätze.",
    longDesc: "Die Firma aus Tschechien übernimmt die Frühjahrsüberholung der Tennisplätze. Die Plätze werden professionell aufbereitet und für die neue Saison vorbereitet.",
    details: [
      { label: "Zeitraum", value: "01.04 – 02.04" },
      { label: "Durchführung", value: "Externe Firma" },
      { label: "Bereich", value: "Tennisplätze" },
    ],
  },
  {
    id: 4,
    title: "Arbeitsdienst",
    date: "11.04, 18.04, 25.04",
    location: "Sportanlage Heinersreuth",
    tag: "Vereinsleben",
    images: [
      "/photos/Image.jpeg",
      "/photos/tennis-court.jpg",
    ],
    icon: <Users className="w-4 h-4" />,
    accentColor: "text-blue-400",
    shortDesc: "Arbeitsdienst auf der Anlage zur Vorbereitung der Saison.",
    longDesc: "Arbeitsdienst auf der Anlage zur Vorbereitung der Saison. Alle Mitglieder sind herzlich eingeladen, bei der Vorbereitung der Anlage zu helfen und gemeinsam die Saison einzuläuten.",
    details: [
      { label: "Termine", value: "11.04, 18.04, 25.04" },
      { label: "Teilnahme", value: "Alle Mitglieder" },
      { label: "Zweck", value: "Saisonvorbereitung" },
    ],
  },
  {
    id: 5,
    title: "Beginn Padel Punktspiele",
    date: "Mitte April",
    location: "Sportanlage Heinersreuth",
    tag: "Punktspiele",
    images: [
      "/photos/padel-on-court.jpg",
      "/photos/padel-playing-side.jpg",
    ],
    icon: <Trophy className="w-4 h-4" />,
    accentColor: "text-red-400",
    shortDesc: "Start der Padel Punktspiele.",
    longDesc: "Mit Mitte April starten die Padel Punktspiele in die neue Saison. Die Mannschaften des SV Heinersreuth treten gegen andere Vereine an.",
    details: [
      { label: "Beginn", value: "Mitte April" },
      { label: "Sportart", value: "Padel" },
      { label: "Format", value: "Punktspiele" },
    ],
  },
  {
    id: 6,
    title: "Platzeröffnung Tennis",
    date: "25.04",
    location: "Sportanlage Heinersreuth",
    tag: "Eröffnung",
    images: [
      "/photos/tennis-court.jpg",
      "/photos/tennis-ball.jpg",
    ],
    icon: <Trophy className="w-4 h-4" />,
    accentColor: "text-green-400",
    shortDesc: "Offizielle Platzeröffnung nach dem Arbeitsdienst.",
    longDesc: "Nach dem abschließenden Arbeitsdienst am 25. April findet die offizielle Platzeröffnung für die Tennissaison statt. Gemeinsam starten wir in die neue Saison!",
    details: [
      { label: "Datum", value: "25.04" },
      { label: "Anlass", value: "Saisoneröffnung" },
      { label: "Teilnahme", value: "Alle Mitglieder" },
    ],
  },
  {
    id: 7,
    title: "Beginn Punktspiele",
    date: "02.05",
    location: "Sportanlage Heinersreuth",
    tag: "Punktspiele",
    images: [
      "/photos/tennis-court.jpg",
      "/photos/padel-on-net.jpg",
    ],
    icon: <Trophy className="w-4 h-4" />,
    accentColor: "text-red-400",
    shortDesc: "Start der Punktspiele für die Sommersaison.",
    longDesc: "Am 02. Mai starten die Punktspiele für die Sommersaison. Alle Mannschaften des SV Heinersreuth treten in ihren jeweiligen Ligen an.",
    details: [
      { label: "Datum", value: "02.05" },
      { label: "Sportart", value: "Tennis" },
      { label: "Format", value: "Punktspiele" },
    ],
  },
  {
    id: 8,
    title: "Jahreshauptversammlung SVH",
    date: "20.05",
    location: "SV Heinersreuth",
    tag: "Vereinsleben",
    images: [
      "/photos/Image.jpeg",
      "/photos/sv.-club.jpg",
    ],
    icon: <Users className="w-4 h-4" />,
    accentColor: "text-blue-400",
    shortDesc: "Jahreshauptversammlung des SV Heinersreuth.",
    longDesc: "Die Jahreshauptversammlung des SV Heinersreuth findet am 22. Mai statt. Alle Mitglieder sind eingeladen, an der Versammlung teilzunehmen und die Zukunft des Vereins mitzugestalten.",
    details: [
      { label: "Datum", value: "20.05" },
      { label: "Veranstalter", value: "SV Heinersreuth" },
      { label: "Teilnahme", value: "Alle Mitglieder" },
    ],
  },
  {
    id: 9,
    title: "Mannschaftsspiele Tennis",
    date: "Sommer 2026",
    location: "Sportanlage Heinersreuth",
    tag: "Turnier",
    images: [
      "/photos/tennis-court.jpg",
      "/photos/tennis-ball.jpg",
    ],
    icon: <Trophy className="w-4 h-4" />,
    accentColor: "text-amber-400",
    shortDesc: "Mannschaftsspiele Tennis mit Vereinen der Spielgemeinschaft.",
    longDesc: "Im Sommer 2026 finden die Mannschaftsspiele Tennis mit Vereinen der Spielgemeinschaft statt. Die Mannschaften messen sich in spannenden Begegnungen und kämpfen um Punkte in der Liga.",
    details: [
      { label: "Zeitraum", value: "Sommer 2026" },
      { label: "Zielgruppe", value: "Jugend" },
      { label: "Format", value: "Vereinsturnier" },
    ],
  },
  {
    id: 2,
    title: "Sommernachts-Open 2026",
    date: "22. Juli 2026",
    location: "Center Court · SV Heinersreuth",
    tag: "Event",
    images: [
      "/photos/padel-playing-side.jpg",
      "/photos/padel-door.jpg",
      "/photos/padel-behind-playing.jpg",
    ],
    icon: <Users className="w-4 h-4" />,
    accentColor: "text-amber-400",
    shortDesc: "Tennis unter LED-Flutlicht mit Chill-Lounge, Drinks & Musik. Für alle Spielstärken.",
    longDesc: "Tennis unter LED-Flutlicht erleben! Unser Sommernachts-Open bietet euch eine einzigartige Atmosphäre mit Chill-Lounge, erfrischenden Drinks und entspannter Musik. Ein lockerer Wettkampf für alle Spielstärken — der perfekte Sommerabend auf dem Platz!",
    details: [
      { label: "Beginn", value: "20:00 Uhr" },
      { label: "Dresscode", value: "Sportlich & Casual" },
      { label: "Eintritt", value: "Frei für Mitglieder" },
    ],
  },
  {
    id: 10,
    title: "Sportplatzkerwa",
    date: "06.08 – 10.08",
    location: "Sportanlage Heinersreuth",
    tag: "Turnier",
    images: [
      "/photos/padel-on-court.jpg",
      "/photos/tennis-court.jpg",
      "/photos/padel-playing-side.jpg",
    ],
    icon: <Trophy className="w-4 h-4" />,
    accentColor: "text-red-400",
    shortDesc: "Turnierwoche auf der Sportplatzkerwa.",
    longDesc: "Von 06. bis 10. August findet die Turnierwoche auf der Sportplatzkerwa statt. Eine ganze Woche voller spannender Matches, toller Atmosphäre und Vereinsgemeinschaft.",
    details: [
      { label: "Zeitraum", value: "06.08 – 10.08" },
      { label: "Format", value: "Turnierwoche" },
      { label: "Teilnahme", value: "Offen für alle" },
    ],
  },
  {
    id: 13,
    title: "Italienische Nacht",
    date: "07.08.2026",
    location: "Sportanlage Heinersreuth",
    tag: "Event",
    images: [
      "/photos/sv-club.jpg",
      "/photos/Image.jpeg",
      "/photos/padel-playing-side.jpg",
    ],
    icon: <Music className="w-4 h-4" />,
    accentColor: "text-amber-400",
    shortDesc: "Ein gesellschaftlicher Sommerabend mit Live-Musik — offen für alle.",
    longDesc: "Am 7. August lädt der SV Heinersreuth zur Italienischen Nacht ein: ein unvergesslicher Sommerabend unter freiem Himmel mit Live-Musik, guter Stimmung und mediterranem Flair. Die Veranstaltung ist offen für alle — Mitglieder und Gäste gleichermaßen. Weitere Details folgen in Kürze.",
    details: [
      { label: "Datum", value: "07.08.2026" },
      { label: "Eintritt", value: "Offen für alle" },
      { label: "Highlight", value: "Live Musik" },
    ],
    showContact: true,
  },
  {
    id: 11,
    title: "Padel Mixed-Turnier",
    date: "19.09.2026",
    location: "Sportanlage Heinersreuth",
    tag: "Turnier",
    images: [
      "/photos/padel-on-net.jpg",
      "/photos/padel-door.jpg",
      "/photos/padel-behind-playing.jpg",
    ],
    icon: <Trophy className="w-4 h-4" />,
    accentColor: "text-red-400",
    shortDesc: "Mixed-Turnier auf den Padel Courts der Anlage — für alle Spielstärken.",
    longDesc: "Am 19. September findet auf der Sportanlage Heinersreuth das Padel Mixed-Turnier statt. Gemischte Teams messen sich in einem spannenden Format auf unseren Padel Courts. Anmeldung und weitere Details über den Kontaktbereich oder direkt beim Verein.",
    details: [
      { label: "Datum", value: "19.09.2026" },
      { label: "Format", value: "Mixed" },
      { label: "Sportart", value: "Padel" },
    ],
    showContact: true,
  },
  {
    id: 12,
    title: "Jahresabschlussfeier",
    date: "14.11",
    location: "SV Heinersreuth",
    tag: "Vereinsleben",
    images: [
      "/photos/Image.jpeg",
      "/photos/sv.-club.jpg",
    ],
    icon: <Users className="w-4 h-4" />,
    accentColor: "text-amber-400",
    shortDesc: "Gemeinsame Jahresabschlussfeier des Vereins.",
    longDesc: "Am 14. November feiern wir gemeinsam die Jahresabschlussfeier des Vereins. Ein geselliger Abend zum Ausklingen der Saison mit allen Mitgliedern.",
    details: [
      { label: "Datum", value: "14.11" },
      { label: "Anlass", value: "Saisonabschluss" },
      { label: "Teilnahme", value: "Alle Mitglieder" },
    ],
  },
];

const EventModal: React.FC<{ event: typeof events[0]; onClose: () => void; onNavigate: (page: string) => void }> = ({ event, onClose, onNavigate }) => {
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setActiveImg(i => (i + 1) % event.images.length);
      if (e.key === "ArrowLeft") setActiveImg(i => (i - 1 + event.images.length) % event.images.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, event.images.length]);

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-8">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="absolute inset-0 bg-black/75 backdrop-blur-lg"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.93, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.93, y: 24 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-2xl bg-white rounded-[28px] shadow-[0_40px_100px_rgba(0,0,0,0.45)] overflow-hidden flex flex-col"
        style={{ maxHeight: "calc(100vh - 4rem)" }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-8 h-8 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/60 transition-colors"
        >
          <X className="w-3.5 h-3.5 text-white" />
        </button>

        <div className="relative flex-shrink-0 h-[42vh] overflow-hidden bg-black">
          <AnimatePresence mode="wait">
            <motion.img
              key={activeImg}
              src={event.images[activeImg]}
              alt={event.title}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/20" />

          {event.images.length > 1 && (
            <>
              <button
                onClick={() => setActiveImg(i => (i - 1 + event.images.length) % event.images.length)}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/60 transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5 text-white" />
              </button>
              <button
                onClick={() => setActiveImg(i => (i + 1) % event.images.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/60 transition-colors"
              >
                <ChevronRight className="w-3.5 h-3.5 text-white" />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {event.images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`transition-all duration-300 rounded-full ${i === activeImg ? "w-5 h-1.5 bg-white" : "w-1.5 h-1.5 bg-white/40 hover:bg-white/65"}`}
                  />
                ))}
              </div>
            </>
          )}

          <div className="absolute bottom-5 left-5">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[9px] font-bold tracking-[0.18em] uppercase bg-white/15 backdrop-blur-sm text-white px-2.5 py-1 rounded-full border border-white/15">{event.tag}</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-white leading-tight max-w-xs drop-shadow-lg">{event.title}</h2>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-5 sm:p-7">
          <div className="flex flex-wrap gap-3 mb-5">
            <div className="flex items-center gap-2 bg-gray-50 border border-[color:var(--svh-border)] px-3.5 py-2 rounded-[10px]">
              <Calendar className="w-3.5 h-3.5 text-[color:var(--svh-crest)] flex-shrink-0" />
              <span className="text-[12px] font-semibold text-gray-800">{event.date}</span>
            </div>
            <div className="flex items-center gap-2 bg-gray-50 border border-[color:var(--svh-border)] px-3.5 py-2 rounded-[10px]">
              <MapPin className="w-3.5 h-3.5 text-[color:var(--svh-crest)] flex-shrink-0" />
              <span className="text-[12px] font-semibold text-gray-800">{event.location}</span>
            </div>
          </div>

          <p className="text-[14px] text-gray-600 leading-relaxed mb-6">{event.longDesc}</p>

          <div className="grid grid-cols-3 gap-3 mb-5">
            {event.details.map((d, i) => (
              <div key={i} className="bg-gray-50 border border-[color:var(--svh-border)] rounded-[16px] p-3.5">
                <p className="text-[9px] text-gray-400 uppercase tracking-[0.14em] mb-1 font-bold">{d.label}</p>
                <p className="text-[12px] font-semibold text-gray-900">{d.value}</p>
              </div>
            ))}
          </div>

          {(event as typeof events[0] & { showContact?: boolean }).showContact ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-4 bg-gray-50 border border-[color:var(--svh-border)] rounded-[16px]">
              <p className="text-[13px] text-[color:var(--svh-ink-3)] flex-1">Für Anmeldung und weitere Infos kontaktiere uns direkt.</p>
              <button
                onClick={() => { onClose(); onNavigate('contact'); }}
                className="flex items-center gap-2 bg-gray-900 hover:bg-red-600 text-white text-[11px] font-bold tracking-[0.12em] uppercase px-4 py-2.5 rounded-[10px] transition-colors duration-300 flex-shrink-0"
              >
                Kontakt
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div className="flex items-start gap-2.5 p-3.5 bg-gray-50 border border-[color:var(--svh-border)] rounded-[16px]">
              <div className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0 mt-1.5" />
              <p className="text-[13px] text-[color:var(--svh-ink-3)]">Weitere Details und Anmeldung folgen in Kürze.</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

const UpcomingEventsPage: React.FC<{ onNavigate?: (page: string) => void }> = ({ onNavigate = () => {} }) => {
  const [selectedEvent, setSelectedEvent] = useState<number | null>(null);
  const selected = events.find(e => e.id === selectedEvent);

  return (
    <PageTransition>
      <div className="min-h-screen bg-white">
        <section className="relative h-[55vh] min-h-[420px] overflow-hidden bg-black">
          <img src="/photos/padel-on-net.jpg" alt="Events" className="absolute inset-0 w-full h-full object-cover scale-[1.06] opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/90" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
          <div className="relative z-10 h-full flex flex-col justify-end pb-16 sm:pb-20 px-6 sm:px-10 max-w-7xl mx-auto w-full">
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="svh-opener__tag mb-6">
                <span className="svh-label" style={{ color: 'rgba(255,255,255,0.62)' }}>SV Heinersreuth</span>
              </div>
              <h1 className="svh-display-xl mb-5" style={{ color: '#FFFFFF' }}>
                Events &amp; Gutscheine
              </h1>
              <p className="svh-lead max-w-lg" style={{ color: 'rgba(255,255,255,0.78)' }}>
                Kommende Turniere, Events und Gutscheine für Padel & Tennis.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="svh-band svh-band--canvas svh-section">
          <div className="max-w-7xl mx-auto px-5 sm:px-10">
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-7 h-[1.5px] bg-[color:var(--svh-crest)]" />
                <span className="svh-eyebrow">Kalender 2026</span>
              </div>
              <h2 className="svh-display-lg">
                Kommende Events
              </h2>
            </div>

            <div className="flex gap-5 overflow-x-auto pb-4 -mx-5 px-5 sm:-mx-10 sm:px-10 scrollbar-hide" style={{ scrollSnapType: 'x mandatory' }}>
              {events.map((event, i) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => setSelectedEvent(event.id)}
                  style={{ scrollSnapAlign: 'start', minWidth: '300px', maxWidth: '300px' }}
                  className="group bg-white rounded-[20px] border border-[color:var(--svh-border)] overflow-hidden cursor-pointer hover:shadow-[0_8px_40px_rgba(0,0,0,0.09)] hover:-translate-y-1 transition-all duration-400 flex-shrink-0"
                >
                  <div className="relative h-52 overflow-hidden bg-black">
                    <img
                      src={event.images[0]}
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className="text-[9px] font-bold tracking-[0.18em] uppercase bg-white/15 backdrop-blur-sm text-white px-3 py-1.5 rounded-full border border-white/15">
                        {event.tag}
                      </span>
                    </div>
                    <div className="absolute bottom-4 left-5">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 text-white/60" />
                        <span className="text-white/80 text-[12px] font-semibold">{event.date}</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-5 sm:p-6">
                    <h3 className="text-[15px] font-bold text-gray-900 mb-2 tracking-tight">{event.title}</h3>
                    <div className="flex items-center gap-2 mb-3">
                      <MapPin className="w-3.5 h-3.5 text-[color:var(--svh-crest)]/70 flex-shrink-0" />
                      <span className="text-[13px] text-[color:var(--svh-ink-3)]">{event.location}</span>
                    </div>
                    <p className="text-[13px] text-gray-500 leading-relaxed mb-5">{event.shortDesc}</p>
                    <div className="flex items-center gap-1.5 text-[12px] font-semibold text-[color:var(--svh-crest)] group-hover:gap-3 transition-all duration-300">
                      Mehr erfahren
                      <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="svh-section svh-canvas border-t border-[color:var(--svh-border)]">
          <div className="max-w-4xl mx-auto px-5 sm:px-10">
            <div className="mb-12 sm:mb-16 text-center">
              <div className="svh-opener__tag mb-6 justify-center">
                <span className="svh-label">Geschenkidee</span>
              </div>
              <h2 className="svh-display-lg mb-4">
                Gutschein kaufen
              </h2>
              <p className="svh-lead max-w-md mx-auto">
                Verschenken Sie Padel & Tennis — ideal für jeden Anlass.
              </p>
            </div>
            <GutscheinKaufen />
          </div>
        </section>

        <AnimatePresence>
          {selected && (
            <EventModal
              key={selected.id}
              event={selected}
              onClose={() => setSelectedEvent(null)}
              onNavigate={onNavigate}
            />
          )}
        </AnimatePresence>
      </div>
    </PageTransition>
  );
};

export default UpcomingEventsPage;
